import {
  type ErrorResponse,
  isRouteErrorResponse,
  Link,
  useParams,
  useRouteError,
  useNavigate,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getHttpErrorPresentation,
  HttpErrorDisplay,
  normalizeRouteErrorData,
} from "#app/components/http-error-display.tsx";
import { Button } from "#app/components/ui/button";
import { getErrorMessage } from "#app/utils/misc.tsx";

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => JSX.Element | null;

export function GeneralErrorBoundary({
  statusHandlers,
}: {
  statusHandlers?: Record<number, StatusHandler>;
} = {}) {
  const error = useRouteError();
  const params = useParams();
  const { t } = useTranslation(["errors", "common"]);

  captureRemixErrorBoundaryError(error);

  if (typeof document !== "undefined") {
    console.error(error);
  }

  const defaultStatusHandler: StatusHandler = ({ error: routeError }) => {
    const message = normalizeRouteErrorData(routeError.data, t);
    const pres = getHttpErrorPresentation(routeError.status, message, t);
    return (
      <HttpErrorDisplay
        status={routeError.status}
        title={pres.title}
        message={pres.description}
        variant={pres.variant}
      />
    );
  };

  const unexpectedErrorHandler = (err: unknown) => (
    <HttpErrorDisplay
      title={t("common:unexpectedError")}
      message={getErrorMessage(err)}
      variant="destructive"
    />
  );

  return (
    <div className="relative flex flex-col items-center justify-center overflow-hidden">
      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}

      <ErrorActions />
    </div>
  );
}

function ErrorActions() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation("common");

  const goBack = () => {
    setIsLoading(true);
    setTimeout(() => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    }, 300);
  };

  return (
    <div className="relative z-10 mt-2 flex flex-wrap items-center justify-center gap-3 px-6 pb-8">
      <Button
        onClick={goBack}
        disabled={isLoading}
        variant="secondary"
        size={isLoading ? "default" : "icon"}
        aria-label={t("goBack")}
        title={t("goBack")}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            {t("goingBack")}
          </span>
        ) : (
          <ArrowLeft className="size-4" />
        )}
      </Button>

      <Button variant="gradient" render={<Link to="/" />}>
        {t("goToHomepage")}
      </Button>
    </div>
  );
}
